import React, { useState } from 'react';
import { GetServerSideProps, NextPage } from 'next';
import { Typography } from '../components/Typography';
import { Button } from '../components/Button';
import styles from './faq.module.scss';
import axios from 'axios';
import { MdAdd, MdRemove } from 'react-icons/md';
import { MdKeyboardArrowDown } from 'react-icons/md';
import PageWrapper from '../components/PageWrapper';
import SEO from '../components/SEO';

interface FaqItem {
  _id: string;
  question: string;
  answer: string;
  category: string;
}

interface FaqPageProps {
  categories: string[];
  faqs: FaqItem[];
  queryId?: string;
  aboutData: any[];
}

const FaqPage: NextPage<FaqPageProps> = ({ categories, faqs, queryId, aboutData }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>(
    categories.length > 0 ? categories[0] : ''
  );
  const [expandedQuestionIndex, setExpandedQuestionIndex] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const filteredFaqs = faqs.filter((faq) => faq.category === selectedCategory);

  const toggleAnswer = (index: number) => {
    setExpandedQuestionIndex(expandedQuestionIndex === index ? null : index);
  };

  return (
    <>
      <SEO
        title="FAQ"
        description="Find answers to frequently asked questions about EPEN Ltd. video plugins, licensing, product setup, and support. Get instant answers to your questions!"
        keywords="faq, frequently asked questions, epen, video plugin, support"
      />
      <PageWrapper
        dateText="FAQs"
        titleText="We're here to help"
        introText="Have questions? We're here to help."
        title="FAQs"
        aboutData={aboutData}
        contentContainerClass={styles.contentContainer}
        additionalContent={
          <>
            {/* Driving Innovation Section */}
            <section className={styles.drivingInnovationSection}>
              <div className={styles.drivingInnovationContent}>
                <div className={styles.drivingInnovationText}>
                  <Typography variant="display-lg" color="primary">
                    Driving Innovation with Precision and Purpose
                  </Typography>

                  <Typography variant="text-xl" color="grayDark-400">
                    At EPEN, we combine creativity, expertise, and dedication to craft technological
                    solutions that empower progress and shape the future.
                  </Typography>
                </div>

                <div className={styles.drivingInnovationButtons}>
                  {/* <Button variant="secondary" label="Learn more" fullWidth /> */}
                  <Button
                    variant="primary"
                    label="Get started"
                    fullWidth
                    onClick={() => {
                      window.location.href = '/about-us';
                    }}
                  />
                </div>
              </div>

              <div className={styles.imageGallery}>
                <img
                  src="/faq-fotter.svg"
                  alt="Frequently Asked Questions about EPEN Ltd. products and support"
                />
              </div>
            </section>
          </>
        }
      >
        <div className={styles.faqPage}>
          <div className={styles.contentSection}>
            <main>
              <section className={styles.faqSection}>
                <div className={styles.faqSectionTitle}>
                  <Typography variant="display-md" color="primary">
                    Frequently asked questions
                  </Typography>

                  <Typography variant="text-xl" color="grayDark-400">
                    Everything you need to know about the product and billing.
                  </Typography>
                </div>

                <div className={styles.tabs}>
                  {categories.map((category) => (
                    <Typography
                      key={category}
                      variant="text-md"
                      as="p"
                      color="secondary"
                      weight="normal"
                      className={`${styles.tabButton} ${
                        selectedCategory === category ? styles.active : ''
                      }`}
                      onClick={() => setSelectedCategory(category)}
                    >
                      {category}
                    </Typography>
                  ))}
                </div>

                <div className={styles.mobiletabs}>
                  <div className={`${styles.dropdownContainer} dropdownContainer`}>
                    <button
                      className={styles.dropdownButton}
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                      <span>{selectedCategory}</span>
                      <MdKeyboardArrowDown
                        size={20}
                        className={`${styles.dropdownIcon} ${isDropdownOpen ? styles.rotated : ''}`}
                      />
                    </button>

                    {isDropdownOpen && (
                      <div className={styles.dropdownMenu}>
                        {categories.map((category) => (
                          <button
                            key={category}
                            className={`${styles.dropdownItem} ${
                              selectedCategory === category ? styles.active : ''
                            }`}
                            onClick={() => {
                              setSelectedCategory(category);
                              setIsDropdownOpen(false);
                            }}
                          >
                            {category}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* FAQ Items */}
                <div className={styles.faqItems}>
                  {filteredFaqs.map((faq, index) => (
                    <div key={faq._id} className={styles.faqItem}>
                      <div className={styles.faqQuestion} onClick={() => toggleAnswer(index)}>
                        <Typography variant="text-md" color="primary">
                          {faq.question}
                        </Typography>
                        <div className={styles.expandIcon}>
                          {expandedQuestionIndex === index ? (
                            <MdRemove size={20} />
                          ) : (
                            <MdAdd size={20} />
                          )}
                        </div>
                      </div>
                      {expandedQuestionIndex === index && (
                        <div className={styles.faqAnswer}>
                          <Typography variant="text-sm" color="grayDark-400">
                            {faq.answer}
                          </Typography>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </main>

            {/* Still Have Questions Section */}
            <section className={styles.stillHaveQuestionsSection}>
              <div className={styles.stillHaveQuestionsContent}>
                <div className={styles.teamImages}>
                  <img
                    src="/get-in-touch.svg"
                    alt="Contact EPEN Ltd. Support Team for Additional Questions"
                  />
                </div>

                <div className={styles.stillHaveQuestionsText}>
                  <Typography variant="text-xl" color="primary">
                    Still have questions?
                  </Typography>

                  <Typography variant="text-lg" color="grayDark-400">
                    Can&apos;t find the answer you&apos;re looking for? Please chat to our friendly
                    team.
                  </Typography>
                </div>

                <Button
                  onClick={() => {
                    window.location.href = '/contact-us';
                  }}
                >
                  Get in touch
                </Button>
              </div>
            </section>
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { id } = context.query;
  let categories: string[] = [];
  let faqs: FaqItem[] = [];
  let aboutData = [];

  try {
    const response = await axios.get(`${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/faqs`);
    categories = response.data.data.categories || [];
    faqs = response.data.data.faqs || [];

    const aboutResponse = await axios.get(
      `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/about-us`
    );
    aboutData = aboutResponse.data.data || [];
  } catch (error) {
    console.error('Error fetching faqs/about-us data for faq page:', error);
  }

  return {
    props: {
      categories,
      faqs,
      queryId: id || null,
      aboutData,
    },
  };
};

export default FaqPage;
